import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listAdminUsers, setUserAdminRole } from "@/lib/admin-console.functions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/_authenticated/admin/users")({
  head: () => ({
    meta: [
      { title: "Users — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UsersPage,
});

function UsersPage() {
  const fetchUsers = useServerFn(listAdminUsers);
  const setRole = useServerFn(setUserAdminRole);
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => fetchUsers(),
  });

  const mutation = useMutation({
    mutationFn: (payload: { userId: string; grant: boolean }) => setRole({ data: payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const users = (data?.users ?? []).filter((u) =>
    q ? u.email.toLowerCase().includes(q.toLowerCase()) : true,
  );

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground">
            Grant or revoke admin access. {data?.users.length ?? 0} accounts.
          </p>
        </div>
        <Input
          placeholder="Search email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {mutation.error && (
        <p className="text-sm text-destructive mb-2">{(mutation.error as Error).message}</p>
      )}

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-medium">Email</th>
              <th className="p-3 font-medium">Roles</th>
              <th className="p-3 font-medium">Confirmed</th>
              <th className="p-3 font-medium">Last sign-in</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isAdmin = u.roles.includes("admin");
              return (
                <tr key={u.id} className="border-t">
                  <td className="p-3">
                    <div className="font-medium">{u.email}</div>
                    <div className="text-xs text-muted-foreground">{u.id}</div>
                  </td>
                  <td className="p-3">
                    {u.roles.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      u.roles.map((r) => (
                        <Badge key={r} variant="outline" className="mr-1">
                          {r}
                        </Badge>
                      ))
                    )}
                  </td>
                  <td className="p-3">
                    {u.email_confirmed_at ? (
                      <span className="text-emerald-600">Yes</span>
                    ) : (
                      <span className="text-amber-600">No</span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {u.last_sign_in_at
                      ? new Date(u.last_sign_in_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      variant={isAdmin ? "outline" : "default"}
                      size="sm"
                      disabled={mutation.isPending}
                      onClick={() =>
                        mutation.mutate({ userId: u.id, grant: !isAdmin })
                      }
                    >
                      {isAdmin ? "Revoke admin" : "Grant admin"}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-muted-foreground">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
